/**
 * Grunnoppsett, arrangementets reservasjoner og husets ressursregister (V2, 0447).
 *
 * ALLE NØKLENE LIGGER UNDER `qk.venueHouse`, og hver skriving feier hele huset og ressurslaget
 * (`qk.resources`). En reservasjon er et forhold til hver ANNEN reservasjon på samme ressurs: å
 * legge baren på ett arrangement endrer konflikten på et annet, i en annen sal og et annet år.
 */
import {
  resourceDeletionSchema,
  venueBaseResourceListSchema,
  venueNightResourcesSchema,
  venueResourceListResponseSchema,
  venueResourceSchema,
  type AddVenueNightResourceRequest,
  type CreateVenueBaseResourceRequest,
  type CreateVenueResourceRequest,
  type ResourceDeletion,
  type UpdateVenueBaseResourceRequest,
  type UpdateVenueResourceRequest,
  type VenueBaseResourceList,
  type VenueNightResources,
  type VenueResource,
  venueGroupListSchema,
  venueGroupSchema,
  type CreateVenueGroupRequest,
  type UpdateVenueGroupRequest,
  type VenueGroup,
} from '@tvitvi/shared';
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const ROOMS = '/venue-rooms';
const NIGHTS = '/venue-nights';
const REGISTER = '/venue-resources';

/** Registeret ber om så mange om gangen; «Vis flere» henter neste side. */
export const VENUE_RESOURCES_PAGE_SIZE = 100;

const invalidates = (orgId: string) => [qk.venueHouse(orgId), qk.resources(orgId)];

/* ------------------------------------------------------------------------------ grunnoppsett */

export function venueBaseResourcesQueryOptions(orgId: string, roomId: string) {
  return queryOptions({
    queryKey: qk.venueHouseBaseResources(orgId, roomId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `${ROOMS}/${roomId}/base-resources`,
        schema: venueBaseResourceListSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

export type BaseResourcePart =
  | { roomId: string; method: 'POST'; body: CreateVenueBaseResourceRequest }
  | { roomId: string; method: 'PATCH'; partId: string; body: UpdateVenueBaseResourceRequest }
  | { roomId: string; method: 'DELETE'; partId: string };

export function useBaseResourcePart(
  orgId: string,
): UseMutationResult<VenueBaseResourceList, unknown, BaseResourcePart> {
  return useMutation({
    mutationFn: (input) =>
      apiRequest({
        path:
          input.method === 'POST'
            ? `${ROOMS}/${input.roomId}/base-resources`
            : `${ROOMS}/${input.roomId}/base-resources/${input.partId}`,
        method: input.method,
        ...(input.method === 'DELETE' ? {} : { body: input.body }),
        schema: venueBaseResourceListSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

/* ------------------------------------------------------------------- arrangementets ressurser */

export function venueNightResourcesQueryOptions(orgId: string, nightId: string) {
  return queryOptions({
    queryKey: qk.venueHouseNightResources(orgId, nightId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/resources`,
        schema: venueNightResourcesSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

export function useAddNightResource(
  orgId: string,
  nightId: string,
): UseMutationResult<VenueNightResources, unknown, AddVenueNightResourceRequest> {
  return useMutation({
    mutationFn: (body) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/resources`,
        method: 'POST',
        body,
        schema: venueNightResourcesSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

export function useRemoveNightResource(
  orgId: string,
  nightId: string,
): UseMutationResult<VenueNightResources, unknown, string> {
  return useMutation({
    mutationFn: (allocationId) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/resources/${allocationId}`,
        method: 'DELETE',
        schema: venueNightResourcesSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

/** «Angre» etter `useRemoveNightResource` (QA6 L3). */
export function useRestoreNightResource(
  orgId: string,
  nightId: string,
): UseMutationResult<VenueNightResources, unknown, string> {
  return useMutation({
    mutationFn: (allocationId) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/resources/${allocationId}/restore`,
        method: 'POST',
        schema: venueNightResourcesSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

export function useNightResourcesFromBase(
  orgId: string,
  nightId: string,
): UseMutationResult<VenueNightResources, unknown, void> {
  return useMutation({
    mutationFn: () =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/resources/from-base`,
        method: 'POST',
        schema: venueNightResourcesSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

/* ------------------------------------------------------------------------- husets register */

export interface VenueResourceFilters {
  q?: string | undefined;
  includeArchived?: boolean | undefined;
  /** B9: gruppen, med undergruppene. */
  groupId?: string | undefined;
}

export function venueResourcesQueryOptions(
  orgId: string,
  festivalId: string,
  filters: VenueResourceFilters,
) {
  return infiniteQueryOptions({
    queryKey: qk.venueHouseResources(orgId, festivalId, { ...filters }),
    queryFn: ({ signal, pageParam }) =>
      apiRequest({
        path: REGISTER,
        query: {
          festivalId,
          limit: VENUE_RESOURCES_PAGE_SIZE,
          sort: 'name',
          direction: 'asc',
          ...(filters.q === undefined || filters.q === '' ? {} : { q: filters.q }),
          ...(filters.includeArchived === true ? { includeArchived: 'true' } : {}),
          ...(filters.groupId === undefined ? {} : { groupId: filters.groupId }),
          ...(pageParam === null ? {} : { cursor: pageParam }),
        },
        schema: venueResourceListResponseSchema,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    ...CACHE.list,
  });
}

export function useCreateVenueResource(
  orgId: string,
): UseMutationResult<VenueResource, unknown, CreateVenueResourceRequest> {
  return useMutation({
    mutationFn: (body) =>
      apiRequest({ path: REGISTER, method: 'POST', body, schema: venueResourceSchema }),
    meta: { invalidates: invalidates(orgId) },
  });
}

export function useUpdateVenueResource(
  orgId: string,
  festivalId: string,
): UseMutationResult<
  VenueResource,
  unknown,
  { resourceId: string; body: UpdateVenueResourceRequest }
> {
  return useMutation({
    mutationFn: ({ resourceId, body }) =>
      apiRequest({
        path: `${REGISTER}/${resourceId}`,
        method: 'PATCH',
        query: { festivalId },
        body,
        schema: venueResourceSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

export function useRemoveVenueResource(
  orgId: string,
  festivalId: string,
): UseMutationResult<ResourceDeletion, unknown, string> {
  return useMutation({
    mutationFn: (resourceId) =>
      apiRequest({
        path: `${REGISTER}/${resourceId}`,
        method: 'DELETE',
        query: { festivalId },
        schema: resourceDeletionSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

export function useRestoreVenueResource(
  orgId: string,
  festivalId: string,
): UseMutationResult<VenueResource, unknown, ResourceDeletion> {
  return useMutation({
    mutationFn: (receipt) =>
      apiRequest({
        path: `${REGISTER}/${receipt.resourceId}/restore`,
        method: 'POST',
        query: { festivalId },
        body: receipt,
        schema: venueResourceSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

/* -------------------------------------------------------------------------- grupper (B9) */

const GROUPS = '/venue-groups';

export function venueGroupsQueryOptions(orgId: string, festivalId: string) {
  return queryOptions({
    queryKey: [...qk.venueHouse(orgId), 'groups', festivalId] as const,
    queryFn: ({ signal }) =>
      apiRequest({ path: GROUPS, query: { festivalId }, schema: venueGroupListSchema, signal }),
    ...CACHE.reference,
  });
}

export function useCreateVenueGroup(
  orgId: string,
): UseMutationResult<VenueGroup, unknown, CreateVenueGroupRequest> {
  return useMutation({
    mutationFn: (body) =>
      apiRequest({ path: GROUPS, method: 'POST', body, schema: venueGroupSchema }),
    meta: { invalidates: invalidates(orgId) },
  });
}

export function useUpdateVenueGroup(
  orgId: string,
  festivalId: string,
): UseMutationResult<VenueGroup, unknown, { groupId: string; body: UpdateVenueGroupRequest }> {
  return useMutation({
    mutationFn: ({ groupId, body }) =>
      apiRequest({
        path: `${GROUPS}/${groupId}`,
        method: 'PATCH',
        query: { festivalId },
        body,
        schema: venueGroupSchema,
      }),
    meta: { invalidates: invalidates(orgId) },
  });
}

export function useRemoveVenueGroup(
  orgId: string,
  festivalId: string,
): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (groupId) =>
      apiRequest({ path: `${GROUPS}/${groupId}`, method: 'DELETE', query: { festivalId } }),
    meta: { invalidates: invalidates(orgId) },
  });
}
